import type { LoadableItems } from '@trimble/data-loading';
import type {
  AlignmentItem,
  FileFolderItem,
  OrthoImageItem,
  PointCloudItem,
} from '@trimble/project-data-panel-types';
import type { ProjectDataRepository } from '@trimble/project-data-repository';
import type {
  TreeItemContextMenuOption,
  TreeNodeDefinition,
  TreeNodeStatus,
} from '@trimble/ui-components/solidjs';
import type { TieringFeatures } from '@trimble/types';
import type { Accessor, JSX } from 'solid-js';
import type { ProjectDataFilterMode } from '../project-data.types';

export type ProjectDataSectionKind =
  | 'models'
  | 'pointClouds'
  | 'alignments'
  | 'orthoImages'
  | 'trajectories'
  | 'missions'
  | 'surfaces';

export type ProjectDataSectionConfig<
  TKind extends ProjectDataSectionKind = ProjectDataSectionKind,
> = TKind extends ProjectDataSectionKind
  ? {
      readonly kind: TKind;
      readonly order?: number;
      readonly multiSelect?: boolean;
      readonly initiallyExpanded?: boolean;
      readonly tiering?: TieringFeatures;
    }
  : never;

export interface ProjectDataPanelConfig {
  readonly sections: readonly ProjectDataSectionConfig[];
  readonly initialFilterMode?: ProjectDataFilterMode;
  readonly filterOptions?: readonly ProjectDataFilterMode[];
  readonly panelTitle?: string;
}

export interface ProjectDataModelSectionOperations {
  isNodeLoading(nodeId: string): boolean;
  isNodeVisible(nodeId: string): boolean;
  onVisibilityToggle(nodeId: string): void;
  onCancelVisibilityToggle?(nodeId: string): void;
  onFitToView?(item: FileFolderItem): void;
  onShowOnly?(item: FileFolderItem): void;
  onSeeDetails?(item: FileFolderItem): void;
  onAddToModelSpace?(item: FileFolderItem): void;
  getValidationContextMenuOptions?(
    item: FileFolderItem
  ): TreeItemContextMenuOption[];
  getItemTrailingContent?(item: FileFolderItem): JSX.Element | null | undefined;
  getValidationNodeStatus?(item: FileFolderItem): TreeNodeStatus | undefined;
  isNodeSelected?(nodeId: string): boolean;
  onNodeSelect?(
    nodeId: string,
    node: TreeNodeDefinition<FileFolderItem>,
    event: MouseEvent
  ): void;
}

export interface ProjectDataPointCloudsSectionOperations {
  isNodeLoading(nodeId: string): boolean;
  isNodeVisible(nodeId: string): boolean;
  onVisibilityToggle(nodeId: string): void;
  onCancelVisibilityToggle?(nodeId: string): void;
  onFitToView?(item: PointCloudItem): void;
  onRenderingSettings?(item: PointCloudItem): void;
  isNodeSelected?(nodeId: string): boolean;
  onNodeSelect?(
    nodeId: string,
    node: TreeNodeDefinition<PointCloudItem>,
    event: MouseEvent
  ): void;
  onShowOnly?(item: PointCloudItem): void;
}

export interface ProjectDataAlignmentsSectionOperations {
  isNodeLoading(nodeId: string): boolean;
  isNodeVisible(nodeId: string): boolean;
  onVisibilityToggle(nodeId: string): void;
  onFitToView?(item: AlignmentItem): void;
  onShowOnly?(item: AlignmentItem): void;
  isNodeSelected?(nodeId: string): boolean;
  onNodeSelect?(
    nodeId: string,
    node: TreeNodeDefinition<AlignmentItem>,
    event: MouseEvent
  ): void;
}

export interface ProjectDataOrthoImagesSectionOperations {
  isNodeLoading(nodeId: string): boolean;
  isNodeVisible(nodeId: string): boolean;
  onVisibilityToggle(nodeId: string): void;
  onDownload?(item: OrthoImageItem): void;
  onSeeDetails?(item: OrthoImageItem): void;
  onShowOnly?(item: OrthoImageItem): void;
  onDelete?(item: OrthoImageItem): void;
  isNodeSelected?(nodeId: string): boolean;
  onNodeSelect?(
    nodeId: string,
    node: TreeNodeDefinition<OrthoImageItem>,
    event: MouseEvent
  ): void;
}

export interface ProjectDataSectionOperationsMap {
  readonly models: ProjectDataModelSectionOperations;
  readonly pointClouds: ProjectDataPointCloudsSectionOperations;
  readonly alignments: ProjectDataAlignmentsSectionOperations;
  readonly orthoImages: ProjectDataOrthoImagesSectionOperations;
  readonly trajectories: object;
  readonly missions: object;
  readonly surfaces: object;
}

export interface ProjectDataSectionDataMap {
  readonly models: undefined;
  readonly pointClouds: LoadableItems<PointCloudItem>;
  readonly alignments: LoadableItems<AlignmentItem>;
  readonly orthoImages: LoadableItems<OrthoImageItem>;
  readonly trajectories: unknown;
  readonly missions: unknown;
  readonly surfaces: unknown;
}

export type ProjectDataPanelOperations = {
  readonly [TKind in ProjectDataSectionKind]?: ProjectDataSectionOperationsMap[TKind];
};

export type ProjectDataPanelSectionData = {
  readonly [TKind in ProjectDataSectionKind]?: ProjectDataSectionDataMap[TKind];
};

export interface ProjectDataSectionRenderContext<
  TKind extends ProjectDataSectionKind,
> {
  readonly repository: ProjectDataRepository;
  readonly config: ProjectDataSectionConfig<TKind>;
  readonly data: Accessor<ProjectDataSectionDataMap[TKind]>;
  readonly operations: ProjectDataSectionOperationsMap[TKind];
}

export interface ProjectDataSectionDefinition<
  TKind extends ProjectDataSectionKind,
> {
  readonly requiresData?: boolean;
  render(context: ProjectDataSectionRenderContext<TKind>): JSX.Element;
}

const sectionDefinitions = new Map<ProjectDataSectionKind, unknown>();

export function registerProjectDataSection<
  TKind extends ProjectDataSectionKind,
>(kind: TKind, definition: ProjectDataSectionDefinition<TKind>): () => void {
  if (sectionDefinitions.has(kind)) {
    throw new Error(`Project data section "${kind}" is already registered.`);
  }

  sectionDefinitions.set(kind, definition);
  return () => {
    if (sectionDefinitions.get(kind) === definition) {
      sectionDefinitions.delete(kind);
    }
  };
}

export function getProjectDataSectionDefinition<
  TKind extends ProjectDataSectionKind,
>(kind: TKind): ProjectDataSectionDefinition<TKind> | undefined {
  return sectionDefinitions.get(kind) as
    | ProjectDataSectionDefinition<TKind>
    | undefined;
}
